import React from 'react';
import { Grid } from '@material-ui/core';
import { SofaFormControl, InputType, getPropsForSofaInput, SofaInput, SofaSelect, SofaCheckbox } from '@sofology/sofologycomponents';

import { SofaSortCodeInput, SofaDateInput, SofaYearsMonthsInputsWrapper } from 'components';
import { SofaFormAddressWrapper, SofaRadioWrapper } from 'components/dynamicFormComponents';
import { IfcAddressDescription, PropertyDescriptionTypes, SelectPropertyDescription } from 'models';
import { nullableValueToString, getOptionsForSofaInput } from 'helpers/dynamicFormHelpers/propertyDescriptionHelpers';
import { getHelperText, getHelperTextType } from 'helpers/sofaFormHelpers';
import { PropertyValueType } from './formHelperAdditionalData';

export const getComponentByInputType = (control: SofaFormControl<PropertyValueType>, property: PropertyDescriptionTypes): JSX.Element => {
    switch (control['inputType']) {
        case InputType.DatePicker:
            return (
                <Grid key={generateKey(property.name)} item xs={12}>
                    <SofaDateInput
                        label={property.label}
                        parentControl={control}
                        description={property.description}
                        {...getPropsForSofaInput(control)}
                        value={nullableValueToString(control.value)}
                        helperText={control.errorMessage}
                    />
                </Grid>
            );
        case InputType.TextInput:
            return (
                <Grid key={generateKey(property.name)} item xs={12}>
                    <SofaInput
                        label={property.label}
                        inputProps={{ autoCorrect: 'off' }}
                        {...getPropsForSofaInput(control)}
                        disabled={property.readonly}
                        value={nullableValueToString(control.value)}
                        helperText={property.readonly ? null : getHelperText(property.description, control.errorMessage)}
                        helperTextType={getHelperTextType(control.errorMessage)}
                        addErrorClass={property.readonly ? false : control.errorMessage !== null}
                    />
                </Grid>
            );
        case InputType.Radio:
            return (
                <Grid key={generateKey(property.name)} item xs={12}>
                    <SofaRadioWrapper
                        label={property.label}
                        {...getPropsForSofaInput(control)}
                        items={getOptionsForSofaInput((property as SelectPropertyDescription).options)}
                        disabled={property.readonly}
                        labelPlacement='start'
                    />
                </Grid>
            );
        case InputType.Selector:
            return (
                <Grid key={generateKey(property.name)} item xs={12}>
                    <SofaSelect
                        label={property.label}
                        {...getPropsForSofaInput(control)}
                        disabled={property.readonly}
                        items={getOptionsForSofaInput((property as SelectPropertyDescription).options)}
                        value={nullableValueToString(control.value)}
                        helperText={property.readonly ? null : control.errorMessage}
                        addErrorClass={property.readonly ? false : control.errorMessage !== null}
                    />
                </Grid>
            );
        case InputType.Checkbox:
            return (
                <Grid key={generateKey(property.name)} item xs={12}>
                    <SofaCheckbox
                        label={property.label}
                        {...getPropsForSofaInput(control)}
                    />
                </Grid>
            );
        default:
            return (<></>);
    }
};

export const getYearsMonthsComponent = (label: string, componentKey: string, controls: SofaFormControl<PropertyValueType>[]): JSX.Element => {
    return (
        <SofaYearsMonthsInputsWrapper
            label={label}
            yearsControl={controls[0]}
            monthsContorl={controls[1]}
            key={componentKey}
        />
    );
};

export const getFieldSetComponent = (label: string, componentKey: string, subComponents: JSX.Element[]): JSX.Element => {
    const labelAsClassName = label.toLowerCase();

    return (
        <div key={generateKey(componentKey)} className={`${labelAsClassName} field-set`}>
            <div className='field-set__header'>
                {label}
            </div>
            <div className='field-set__body'>
                {subComponents}
            </div>
        </div>
    );
};

export const getAddressComponent = (label: string, addressControl: SofaFormControl<string>, addressProperties: IfcAddressDescription, globalState: any, readonly: boolean): JSX.Element => {
    return (
        <Grid key={addressControl.name} item xs={12}>
            <SofaFormAddressWrapper
                key={addressControl.name}
                label={label}
                ifcAddressQuestionSet={addressProperties as unknown as IfcAddressDescription}
                globalState={globalState}
                parentControl={addressControl}
                readonly={readonly}
                {...getPropsForSofaInput(addressControl)}
                gtmFormName={addressControl.name}
                index={addressControl.index}
            />
        </Grid>
    );
};

export const getSortCodeComponent = (sortCodeProperty: PropertyDescriptionTypes, sortCodeControl: SofaFormControl<PropertyValueType>): JSX.Element => {
    return <Grid key={generateKey(sortCodeProperty.name)} item xs={12}>
        <SofaSortCodeInput
            label={sortCodeProperty.label}
            parentControl={sortCodeControl}
            {...getPropsForSofaInput(sortCodeControl)}
            value={nullableValueToString(sortCodeControl.value)}
            helperText={sortCodeControl.errorMessage}
        />
    </Grid>;
};

const generateKey = (componentName: string): string => {
    const fixedName = componentName.toLowerCase().replace(' ', '-');

    return `${fixedName}`;
};